// src/features/auth/pages/LoginPage.jsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Checkbox,
    FormControlLabel,
    Alert,
    useTheme,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    AccountCircle,
    LockOutlined,
    ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logoImage from '../../../assets/logo-agence-vg.png';
import apiClient from '../../../services/api-client';
import { useAuthStore } from '../../../store/useAuthStore';

// ── Motif décoratif SVG (lignes géométriques dorées) ─────────────────────────
function GeoPattern() {
    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 600 800"
            preserveAspectRatio="xMidYMid slice"
            style={{ position: 'absolute', inset: 0, opacity: 0.12 }}
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Grille de losanges */}
            {Array.from({ length: 8 }).map((_, col) =>
                Array.from({ length: 11 }).map((_, row) => (
                    <rect
                        key={`${col}-${row}`}
                        x={col * 80 - 20}
                        y={row * 80 - 20}
                        width={50}
                        height={50}
                        fill="none"
                        stroke="#c9a84c"
                        strokeWidth="0.8"
                        transform={`rotate(45, ${col * 80 + 5}, ${row * 80 + 5})`}
                    />
                ))
            )}
            {/* Grandes diagonales */}
            <line x1="0" y1="0" x2="600" y2="800" stroke="#c9a84c" strokeWidth="0.5" />
            <line x1="600" y1="0" x2="0" y2="800" stroke="#c9a84c" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="600" y2="600" stroke="#c9a84c" strokeWidth="0.3" />
            <line x1="0" y1="600" x2="600" y2="200" stroke="#c9a84c" strokeWidth="0.3" />
            {/* Cercle central décoratif */}
            <circle cx="300" cy="400" r="180" fill="none" stroke="#c9a84c" strokeWidth="0.6" />
            <circle cx="300" cy="400" r="120" fill="none" stroke="#c9a84c" strokeWidth="0.4" />
            <circle cx="300" cy="400" r="60" fill="none" stroke="#c9a84c" strokeWidth="0.8" />
        </svg>
    );
}

// ── Composant principal ───────────────────────────────────────────────────────
export function LoginPage() {
    const theme = useTheme();
    const navigate = useNavigate();

    const { isAuthenticated, setAuth } = useAuthStore();

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/connecter', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // const setAuth = useAuthStore((state) => state.setAuth);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await apiClient.post('/api/auth/login', {
                username: username,
                password: password,
                rememberMe: remember
            });

            setAuth(response.data);

            if (response.data.user.mustChangePassword) {
                navigate('/change-password', { replace: true });
            } else {
                navigate('/connecter', { replace: true });
            }
        } catch (err) {
            console.error("Erreur login:", err);
            const serverMessage = err.response?.data?.message;
            setError(serverMessage || 'Identifiants ou mot de passe incorrects.');
        } finally {
            setLoading(false);
        }
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.9rem',
            transition: 'all 0.2s ease',
            '& fieldset': {
                borderColor: 'rgba(26,26,46,0.15)',
                transition: 'border-color 0.2s ease',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(201,168,76,0.4)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#c9a84c',
                borderWidth: '1.5px',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#c9a84c',
        },
    };

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                fontFamily: "'DM Sans', sans-serif",
                overflow: 'hidden',
            }}
        >
            {/* ── Panneau gauche : Brand ── */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    flex: '0 0 48%',
                    position: 'relative',
                    bgcolor: '#1a1a2e',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 6,
                    overflow: 'hidden',
                }}
            >
                {/* Motif géométrique en fond */}
                <GeoPattern />

                {/* Halo doré central */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 420,
                        height: 420,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(201,168,76,0.12) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }}
                />

                {/* Logo en haut */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: '2rem',
                            fontWeight: 700,
                            color: '#c9a84c',
                            lineHeight: 1,
                        }}
                    >
                        Miracle Travel
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.3)',
                            letterSpacing: 4,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            mt: 0.8,
                        }}
                    >
                        TOURS & SERVICES
                    </Typography>
                </Box>

                {/* Citation centrale */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Guillemet décoratif */}
                    <Typography
                        sx={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: '8rem',
                            color: '#c9a84c',
                            opacity: 0.25,
                            lineHeight: 0.8,
                            mb: -2,
                            userSelect: 'none',
                        }}
                    >
                        "
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: '1.85rem',
                            fontWeight: 600,
                            color: 'white',
                            lineHeight: 1.35,
                            maxWidth: 380,
                        }}
                    >
                        Chaque voyage commence par une décision. Gérez les vôtres avec élégance.
                    </Typography>
                    <Box
                        sx={{
                            mt: 3,
                            width: 48,
                            height: 2,
                            bgcolor: '#c9a84c',
                            borderRadius: 1,
                        }}
                    />
                </Box>

                {/* Footer du panneau */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.2)',
                            fontSize: '0.7rem',
                            letterSpacing: 1,
                        }}
                    >
                        © {new Date().getFullYear()} Miracle Travel. Tous droits réservés.
                    </Typography>
                </Box>
            </Box>

            {/* ── Panneau droit : Formulaire ── */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafaf8',
                    p: { xs: 3, sm: 6 },
                    position: 'relative',
                }}
            >
                {/* Accent doré en haut à droite */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 200,
                        height: 200,
                        background: 'radial-gradient(circle at top right, rgba(201,168,76,0.08) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }}
                />

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {/* Logo mobile uniquement */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 4, textAlign: 'center' }}>
                        <Typography
                            sx={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: '1.8rem',
                                fontWeight: 700,
                                color: '#1a1a2e',
                            }}
                        >
                            Miracle Travel
                        </Typography>
                        <Typography sx={{ color: '#c9a84c', fontSize: '0.6rem', letterSpacing: 4, fontWeight: 600 }}>
                            TOURS & SERVICES
                        </Typography>
                    </Box>

                    {/* En-tête du formulaire */}
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box component="img" src={logoImage} alt="Login" sx={{ width: '70px', height: 'auto', mb: 1, alignSelf: 'center' }} />
                        <Typography
                            sx={{
                                color: 'rgba(26,26,46,0.5)',
                                fontSize: '0.88rem',
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                        >
                            Connectez-vous pour accéder à votre espace de gestion.
                        </Typography>
                    </Box>

                    {/* Message d'erreur */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: '12px',
                                fontSize: '0.82rem',
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Champ Email */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography
                            sx={{
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                color: '#1a1a2e',
                                mb: 1,
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                            }}
                        >
                            Nom d'utilisateur
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="votre nom d'utilisateur"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircle
                                            fontSize="small"
                                            sx={{ color: username ? '#c9a84c' : 'rgba(26,26,46,0.3)', transition: 'color 0.2s' }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            required
                            sx={inputSx}
                        />
                    </Box>

                    {/* Champ Mot de passe */}
                    <Box sx={{ mb: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                color: '#1a1a2e',
                                mb: 1,
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                            }}
                        >
                            Mot de passe
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="••••••••"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined
                                            fontSize="small"
                                            sx={{ color: password ? '#c9a84c' : 'rgba(26,26,46,0.3)', transition: 'color 0.2s' }}
                                        />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            sx={{ color: 'rgba(26,26,46,0.3)', '&:hover': { color: '#c9a84c' } }}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            required
                            sx={inputSx}
                        />
                    </Box>

                    {/* Se souvenir + Mot de passe oublié */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 4,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                    size="small"
                                    sx={{
                                        color: 'rgba(26,26,46,0.2)',
                                        '&.Mui-checked': { color: '#c9a84c' },
                                        p: 0.5,
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(26,26,46,0.6)', fontFamily: "'DM Sans', sans-serif" }}>
                                    Se souvenir de moi
                                </Typography>
                            }
                        />
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                color: '#c9a84c',
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: "'DM Sans', sans-serif",
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            Mot de passe oublié ?
                        </Typography>
                    </Box>

                    {/* Bouton de connexion */}
                    <Button
                        type="submit"
                        fullWidth
                        disabled={loading}
                        endIcon={!loading && <ArrowForward sx={{ fontSize: '1rem !important' }} />}
                        sx={{
                            height: 52,
                            borderRadius: '14px',
                            bgcolor: '#1a1a2e',
                            color: 'white',
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            letterSpacing: '0.04em',
                            textTransform: 'none',
                            boxShadow: '0 8px 24px rgba(26,26,46,0.25)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(135deg, rgba(201,168,76,0.15) 0%, transparent 60%)',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                            },
                            '&:hover': {
                                bgcolor: '#1a1a2e',
                                boxShadow: '0 12px 32px rgba(26,26,46,0.35)',
                                transform: 'translateY(-1px)',
                                '&::before': { opacity: 1 },
                            },
                            '&:active': {
                                transform: 'translateY(0)',
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(26,26,46,0.5)',
                                color: 'rgba(255,255,255,0.6)',
                            },
                        }}
                    >
                        {loading ? 'Connexion en cours...' : 'Se connecter'}
                    </Button>

                    {/* Ligne décorative */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            my: 4,
                            gap: 2,
                        }}
                    >
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(26,26,46,0.08)' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(26,26,46,0.35)', letterSpacing: 2, fontWeight: 600 }}>
                            MIRACLE TRAVEL
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(26,26,46,0.08)' }} />
                    </Box>

                    {/* Lien d'aide */}
                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontSize: '0.8rem',
                            color: 'rgba(26,26,46,0.45)',
                            fontFamily: "'DM Sans', sans-serif",
                        }}
                    >
                        Un problème de connexion ?{' '}
                        <Typography
                            component="span"
                            sx={{
                                color: '#c9a84c',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            Contacter le support
                        </Typography>
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}